/**
 * The on-disk shape of a base folder under `wayai-ws/bases/<base>/`.
 *
 * Deliberately loose structural mirrors of the Data surface's config and diff
 * payloads: this layer only has to round-trip YAML and render a diff, and the
 * authoritative schema lives behind the `/v1` surface.
 *
 * These types ARE the writer's allowlist: `config-writer.ts` emits exactly the
 * keys declared here, in a fixed order, and a typecheck fails if a field added
 * here is not given a place in that order. A field the backend adds therefore
 * needs a line here before it round-trips — which is the deliberate trade for
 * never writing a `secret` the payload carries into a user's repo.
 *
 * `base.yaml`'s key order and the entity subdir names are the standalone Data
 * CLI's, unchanged — the folder moves under `wayai-ws/`, its contents do not.
 */

export interface RecordTypeConfig {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  json_schema: Record<string, unknown>;
  ui?: Record<string, unknown>;
  sources?: unknown[];
}

export interface RelationshipTypeConfig {
  id: string;
  description?: string;
  data_schema?: unknown;
  source_record_types?: string[];
  target_record_types?: string[];
  cascade?: boolean;
}

/**
 * A file type: the storage policy and optional metadata schema of a set of files. Its `id` is the
 * spelling the file routes address. The server's `version` and timestamps are not config, so they
 * are not listed here and never written to disk.
 */
export interface FileTypeConfig {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  /** Max bytes per file; `0` = unlimited. */
  max_size?: number;
  /** Allowed content types; empty = any. */
  allowed_content_types?: string[];
  /** JSON Schema each file's metadata must match; `null` = unconstrained. */
  metadata_schema?: unknown;
}

export interface InboundWebhookConfig {
  id: string;
  name: string;
  record_type_scope?: string[] | null;
  field_mapping?: Record<string, unknown> | null;
  source_binding?: Record<string, unknown> | null;
  ingest_auth?: Record<string, unknown> | null;
  hydration?: Record<string, unknown> | null;
  enabled?: boolean;
}

export interface TriggerConfig {
  id: string;
  name: string;
  action?: Record<string, unknown>;
  /** Legacy flat webhook url, kept for back-compat; `action` is canonical. */
  url?: string;
  events: string[];
  record_type_scope?: string[] | null;
  filter?: Record<string, unknown> | null;
  skip_inbound_webhook_writes?: boolean;
  skip_cascade_writes?: boolean;
  enabled?: boolean;
}

export interface ToolsetConfig {
  id: string;
  name: string;
  description?: string;
  actions?: unknown[];
  relationships?: unknown[];
  /** File tools: one entry per file type and folder (`file_type`, `path_prefix`, `operations`, …). */
  files?: unknown[];
  batch?: unknown;
  sql_query?: boolean;
  /** Serves one customer per call: every record type it reaches declares `x-customer-scope`. */
  customer_scope?: boolean;
}

export interface ActionConfig {
  id: string;
  record_type?: string;
  operation?: string;
  /** Composite action: ordered write steps. Mutually exclusive with record_type/operation. */
  steps?: unknown[];
  description?: string;
  external_source?: string;
  writable_fields?: unknown[];
  precondition?: Record<string, unknown>;
  binding?: string;
  filterable_fields?: unknown[];
  expose_filter?: boolean;
  expose_sort?: boolean;
  default_sort?: unknown[];
  expose_limit?: boolean;
  default_limit?: number;
  expose_offset?: boolean;
  expose_fields?: boolean;
  default_fields?: string[] | string;
  agent_minimal?: boolean;
  base_filter?: Record<string, unknown>;
}

export interface SeedConfig {
  id: string;
  description?: string;
  records?: unknown[];
  relationships?: unknown[];
  /** Record types the seed is authoritative for: non-declared rows of these types are pruned. */
  exclusive_record_types?: string[];
}

/** The full desired state of a base's config — what a folder parses to and a pull writes. */
export interface BaseConfig {
  record_types: RecordTypeConfig[];
  relationship_types: RelationshipTypeConfig[];
  /**
   * Absent when the folder has no `file-types/` directory: a push that leaves the section out keeps
   * the base's file types as they are (#5083 decision 169), where an empty list would show every one
   * removed — and `--prune` deletes a file type with its files. A folder pulled before the CLI wrote
   * file types has no such directory.
   */
  file_types?: FileTypeConfig[];
  inbound_webhooks: InboundWebhookConfig[];
  triggers: TriggerConfig[];
  toolsets: ToolsetConfig[];
  actions: ActionConfig[];
  seeds: SeedConfig[];
}

/** Persisted in `<base-folder>/base.yaml`. `base_id` is absent for an as-yet-uncreated preview. */
export interface BaseMeta {
  base_id?: string;
  origin_base_id?: string;
  name?: string;
  description?: string;
  environment?: string;
  /** External-integration toggle (enabled | disabled). Preview-only, enforced server-side. */
  integrations?: string;
  /**
   * Data-plane analytics tier. Load-bearing on ONE path: push's create branch
   * forwards it to the clone route, because the tier is fixed at creation and a
   * base minted on the wrong one can only be replaced, never corrected. For an
   * existing base it is descriptive — the next pull overwrites it.
   */
  analytics?: string;
}

export interface DiffBucket<T> {
  added: T[];
  modified: { before: T; after: T }[];
  removed: T[];
}

/** Advisory tool-design warning from a push or diff. Never blocking. */
export interface LintWarning {
  action: string;
  rule: string;
  message: string;
}

/**
 * Every bucket is optional: the surface omits kinds a diff has nothing to say
 * about, and treating some as required would only mean a non-null assertion at
 * each read while a genuinely-absent key still compiled.
 */
export interface ConfigDiff {
  record_types?: DiffBucket<RecordTypeConfig>;
  relationship_types?: DiffBucket<RelationshipTypeConfig>;
  file_types?: DiffBucket<FileTypeConfig>;
  inbound_webhooks?: DiffBucket<InboundWebhookConfig>;
  triggers?: DiffBucket<TriggerConfig>;
  toolsets?: DiffBucket<ToolsetConfig>;
  actions?: DiffBucket<ActionConfig>;
  seeds?: DiffBucket<SeedConfig>;
  warnings?: LintWarning[];
}

/** The config entity kinds, in the stable order used for folders, files and diff rendering. */
export const ENTITY_KINDS = [
  'record_types',
  'relationship_types',
  'file_types',
  'inbound_webhooks',
  'triggers',
  'toolsets',
  'actions',
  'seeds',
] as const;
export type EntityKind = (typeof ENTITY_KINDS)[number];

/**
 * Folder name on disk for each entity kind (kebab-case for the hyphenated ones)
 * — and, by deliberate coincidence, the `/v1` path segment for that kind, which
 * `deleteEntity` builds from it. The two namespaces agree today; if they ever
 * diverge, split this into a second map rather than renaming one in place.
 */
export const ENTITY_DIRS: Record<EntityKind, string> = {
  record_types: 'record-types',
  relationship_types: 'relationship-types',
  file_types: 'file-types',
  inbound_webhooks: 'inbound-webhooks',
  triggers: 'triggers',
  toolsets: 'toolsets',
  actions: 'actions',
  seeds: 'seeds',
};

/**
 * Folder names for entity kinds that no longer exist. The writer removes these
 * on every write so a pull leaves no dead-weight dirs from a superseded schema.
 * Add a name here (never remove one) when a kind is renamed or dropped; the set
 * must never intersect the values of `ENTITY_DIRS`.
 */
export const DEPRECATED_ENTITY_DIRS: readonly string[] = ['endpoints', 'collections', 'tools'];

/** The config type for one entity kind — what `BaseConfig[K]` holds an array of. */
export type EntityConfig<K extends EntityKind> = NonNullable<BaseConfig[K]>[number];
